import React, { useState, useEffect } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import { it } from "date-fns/locale";
import "react-datepicker/dist/react-datepicker.css";
import * as Utils from "../utils";
import { api, ApiError } from "../api/client";
import { useMessage } from "../components/MessageProvider";

registerLocale("it", it);

const ReadOnlyDateInput = React.forwardRef(
  ({ value, onClick, placeholder, className, inputId, required }, ref) => (
    <input
      id={inputId}
      readOnly
      value={value || ""}
      onClick={onClick}
      ref={ref}
      placeholder={placeholder}
      className={className}
      required={required}
    />
  )
);

const dateInputClasses =
  "block w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:ring-2 focus:ring-primary focus:border-primary transition bg-white cursor-pointer";

function sommaResiduo(base = 0, previsto = 0) {
  const baseNum = Number(base);
  const previstoNum = Number(previsto);
  const totale = (isNaN(baseNum) ? 0 : baseNum) + (isNaN(previstoNum) ? 0 : previstoNum);
  return Math.round(totale * 10) / 10;
}

function RichiesteNuovo({ user, setUser, isOnline, onDone }) {
  const { showMessage } = useMessage();
  const [tipologie, setTipologie] = useState([]);
  const [selectedTipologiaId, setSelectedTipologiaId] = useState("");
  const [selectedTipologia, setSelectedTipologia] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [note, setNote] = useState("");
  const [valore, setValore] = useState("");
  const [residui, setResidui] = useState(user?.residui);

  useEffect(() => {
    async function caricaTipologie() {
      let tipologieData = [];
      if (Utils.isOnline()) {
        try {
          const data = await api.post('/api/tipologie', { session_token: user.token });
          tipologieData = Array.isArray(data) ? data : data?.tipologie || [];
          setTipologie(tipologieData);
          await Utils.saveTipologieOffline(tipologieData);
        } catch (err) {
          console.error("Errore caricamento tipologie:", err);
          tipologieData = await Utils.getTipologieOffline();
          setTipologie(tipologieData);
        }
      } else {
        tipologieData = await Utils.getTipologieOffline();
        setTipologie(tipologieData);
      }
      const ultima = await Utils.getUltimaTipologiaUtilizzata();
      if (ultima) setSelectedTipologiaId(ultima.id);
    }
    caricaTipologie();
  }, []);  // eslint-disable-line

  useEffect(() => {
    const found = tipologie.find(
      (t) => String(t.id) === String(selectedTipologiaId)
    );
    setSelectedTipologia(found || null);
    setValore("");
  }, [selectedTipologiaId, tipologie]);

  useEffect(() => {
    async function updateResidui() {
      if (isOnline && user?.assunzione_id) {
        const userAggiornato = await Utils.aggiornaResidui(user);
        const residuiDalBackend = userAggiornato.residui || {};
        const residuiNormalizzati = {
          ...residuiDalBackend,
          ferie: Number(residuiDalBackend.ferie) || 0,
          permessi: Number(residuiDalBackend.permessi) || 0,
          ex_festivita: Number(residuiDalBackend.ex_festivita) || 0,
          previsto_ferie: Number(residuiDalBackend.previsto_ferie) || 0,
          previsto_permessi: Number(residuiDalBackend.previsto_permessi) || 0,
          previsto_ex_festivita: Number(residuiDalBackend.previsto_ex_festivita) || 0,
        };
        setResidui(residuiNormalizzati);
        setUser && setUser({ ...userAggiornato, residui: residuiNormalizzati });
        localStorage.setItem(
          "easyjob_user",
          JSON.stringify({ ...userAggiornato, residui: residuiNormalizzati, password: user.password })
        );
        await Utils.saveUserOffline({
          ...userAggiornato,
          residui: residuiNormalizzati,
          password: user.password,
          username: user.username,
        });
      }
    }
    updateResidui();
  }, []);  // eslint-disable-line

  function resetForm(reset_tipologia = true) {
    if (reset_tipologia) setSelectedTipologiaId("");
    setStartDate(null);
    setEndDate(null);
    setNote("");
    setValore("");
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTipologiaId || !startDate || !endDate) return;

    let valoreNormalizzato = valore;
    if (selectedTipologia?.unita_di_misura) {
      valoreNormalizzato = valore.replace(",", ".");
      if (isNaN(valoreNormalizzato) || valoreNormalizzato === "") {
        await showMessage({ title: "Attenzione", message: "Inserisci un valore numerico valido.", type: "error", timeout: 3 });
        return;
      }
    }

    const payload = {
      richiesta_tipologia_id: selectedTipologiaId,
      data_dal: Utils.formatIsoDate(startDate),
      data_al: Utils.formatIsoDate(endDate),
      valore: selectedTipologia?.unita_di_misura
        ? valoreNormalizzato === "" ? 0 : Number(valoreNormalizzato)
        : 0,
      note,
      user_richiesta_id: user.id,
      cliente_id: user.cliente_id,
      assunzione_id: user.assunzione_id,
      session_token: user.token,
    };

    if (!Utils.isOnline()) {
      await Utils.saveRequestOffline(payload);
      await Utils.setUltimaTipologiaUtilizzata(selectedTipologiaId, selectedTipologia?.descrizione);
      await showMessage({
        title: "Richiesta salvata",
        message: "Richiesta salvata offline. Verrà inviata appena torna la connessione.",
        type: "success",
        timeout: 3,
      });
      resetForm(false);
      onDone && onDone();
      return;
    }

    try {
      const data = await api.post("/api/richieste", payload);
      if (data?.success) {
        try {
          await Utils.setUltimaTipologiaUtilizzata(selectedTipologiaId, selectedTipologia?.descrizione);
        } catch (err) {
          console.warn("Errore nel salvataggio preferenza tipologia:", err);
        }
        await showMessage({ title: "Richiesta inviata", message: "Richiesta inviata con successo!", type: "success", timeout: 2 });
        onDone && onDone();
      } else {
        await showMessage({ title: "Errore", message: "Errore nell'invio della richiesta.", type: "error", timeout: 0 });
      }
    } catch (err) {
      if (err instanceof ApiError && !err.isNetworkError) {
        await showMessage({ title: "Errore", message: err.body?.message || err.message || "Errore nell'invio della richiesta.", type: "error", timeout: 0 });
      } else {
        await showMessage({ title: "Errore", message: "Errore di rete.", type: "error", timeout: 0 });
      }
    }
  };

  return (
    <div style={{ height: "calc(100dvh - 3.5rem - env(safe-area-inset-top))", overflowY: "auto", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div className="w-full bg-gray-100 flex flex-col items-center">
        <form
          className="bg-white p-4 sm:p-6 rounded-b-2xl shadow-lg w-full max-w-2xl mx-auto flex flex-col gap-4 border border-gray-100 sm:gap-6 min-h-[80vh]"
          onSubmit={handleSubmit}
        >
          {residui && (
            <div className="w-full mb-2">
              <div className="flex flex-wrap justify-center gap-4">
                <div
                  className="bg-blue-50 rounded-lg shadow px-6 py-3 text-center min-w-[140px] flex-1 cursor-pointer hover:ring-2 hover:ring-blue-300 transition"
                  onClick={() => window.setSection && window.setSection("infoUtente")}
                  title="Vedi dettagli utente"
                >
                  <div className="text-xs text-blue-700 font-semibold">Ore Ferie</div>
                  <div className="text-2xl font-bold text-blue-900">
                    {sommaResiduo(residui.ferie, residui.previsto_ferie)}
                  </div>
                </div>
                <div
                  className="bg-green-50 rounded-lg shadow px-6 py-3 text-center min-w-[140px] flex-1 cursor-pointer hover:ring-2 hover:ring-green-300 transition"
                  onClick={() => window.setSection && window.setSection("infoUtente")}
                  title="Vedi dettagli utente"
                >
                  <div className="text-xs text-green-700 font-semibold">Ore Permessi</div>
                  <div className="text-2xl font-bold text-green-900">
                    {sommaResiduo(residui.permessi, residui.previsto_permessi)}
                  </div>
                </div>
                <div
                  className="bg-orange-soft rounded-lg shadow px-6 py-3 text-center min-w-[140px] flex-1 cursor-pointer hover:ring-2 hover:ring-orange-300 transition"
                  onClick={() => window.setSection && window.setSection("infoUtente")}
                  title="Vedi dettagli utente"
                >
                  <div className="text-xs text-orange-700 font-semibold">Ore Ex Festività</div>
                  <div className="text-2xl font-bold text-orange-900">
                    {sommaResiduo(residui.ex_festivita, residui.previsto_ex_festivita)}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 text-right mt-2 px-1 italic">
                *previsione di maturazione fino a fine anno corrente.
              </p>
            </div>
          )}

          <div className="flex flex-col gap-1">
            <label className="font-medium text-primary text-sm sm:text-base" htmlFor="tipo">
              Tipo richiesta <span className="text-red-500">*</span>
            </label>
            <select
              id="tipo"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 text-base"
              value={selectedTipologiaId}
              onChange={(e) => setSelectedTipologiaId(e.target.value)}
              required
            >
              {!selectedTipologiaId && <option value="">Seleziona...</option>}
              {tipologie.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.descrizione}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-medium text-primary text-sm sm:text-base" htmlFor="dal">
              Data inizio <span className="text-red-500">*</span>
            </label>
            <DatePicker
              id="dal"
              selected={startDate}
              onChange={setStartDate}
              locale="it"
              dateFormat="dd/MM/yyyy"
              placeholderText="Seleziona la data di inizio..."
              required
              customInput={
                <ReadOnlyDateInput
                  inputId="dal"
                  placeholder="Seleziona la data di inizio..."
                  className={dateInputClasses}
                  required
                />
              }
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-medium text-primary text-sm sm:text-base" htmlFor="al">
              Data fine <span className="text-red-500">*</span>
            </label>
            <DatePicker
              id="al"
              selected={endDate}
              onChange={setEndDate}
              locale="it"
              dateFormat="dd/MM/yyyy"
              placeholderText="Seleziona la data di fine..."
              required
              minDate={startDate}
              customInput={
                <ReadOnlyDateInput
                  inputId="al"
                  placeholder="Seleziona la data di fine..."
                  className={dateInputClasses}
                  required
                />
              }
            />
          </div>

          <div className="flex flex-col gap-1 min-h-[64px]">
            {selectedTipologia?.unita_di_misura ? (
              <>
                <label className="font-medium text-primary text-sm sm:text-base" htmlFor="valore">
                  {selectedTipologia.unita_di_misura} <span className="text-red-500">*</span>
                </label>
                <input
                  id="valore"
                  type="text"
                  inputMode="decimal"
                  className="block w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:ring-2 focus:ring-primary focus:border-primary transition"
                  value={valore}
                  onChange={(e) => setValore(e.target.value)}
                  required
                  placeholder="Inserisci il valore..."
                />
              </>
            ) : (
              <div className="opacity-0 pointer-events-none select-none">
                <label className="font-medium text-primary">Valore</label>
                <input className="block w-full border rounded-lg px-3 py-3" disabled />
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="font-medium text-primary text-sm sm:text-base" htmlFor="note">
              Note (opzionale)
            </label>
            <textarea
              id="note"
              className="block w-full border border-gray-300 rounded-lg px-3 py-3 text-base focus:ring-2 focus:ring-primary focus:border-primary resize-none transition"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Aggiungi una nota..."
            />
          </div>

          <button
            type="submit"
            className="bg-primary text-white py-4 px-4 rounded-lg font-semibold shadow hover:bg-blue-800 transition w-full mt-2 text-lg"
          >
            INVIA RICHIESTA
          </button>

          <div className="h-16" />
        </form>
      </div>
    </div>
  );
}

export default RichiesteNuovo;
